import { useRef, useState } from 'react'
import { HiRefresh, HiClipboardCopy, HiCheck } from 'react-icons/hi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import StrengthMeter from '../components/StrengthMeter'
import { copyToClipboard } from '../utils/helpers'
import { useDebouncedEffect } from '../hooks/useDebounced'

const CHARSETS = [
  { key: 'useLowercase', label: 'a–z' },
  { key: 'useUppercase', label: 'A–Z' },
  { key: 'useDigits',    label: '0–9' },
  { key: 'useSymbols',   label: '!@#' },
]

function Generator() {
  const [password, setPassword] = useState('')
  const [analysis, setAnalysis] = useState(null)
  const [copied, setCopied]     = useState(false)
  const [nonce, setNonce]       = useState(0) // bump to force a regenerate
  const requestSeq = useRef(0)

  const [options, setOptions] = useState({
    type:         'password',
    length:       20,
    useLowercase: true,
    useUppercase: true,
    useDigits:    true,
    useSymbols:   true,
    wordCount:    4,
  })

  const set = (key, value) => setOptions(prev => ({ ...prev, [key]: value }))
  const checkedCount = CHARSETS.filter(({ key }) => options[key]).length

  // Regenerate whenever options change (and once on mount)
  useDebouncedEffect(() => {
    const seq = ++requestSeq.current
    const payload = options.type === 'passphrase'
      ? { type: 'passphrase', word_count: options.wordCount }
      : {
          type:          'password',
          length:        options.length,
          use_lowercase: options.useLowercase,
          use_uppercase: options.useUppercase,
          use_digits:    options.useDigits,
          use_symbols:   options.useSymbols,
        }

    api.post('/generate', payload)
      .then(res => {
        if (seq !== requestSeq.current) return
        setPassword(res.data.password)
        setAnalysis(res.data.analysis)
        setCopied(false)
      })
      .catch(() => {
        if (seq !== requestSeq.current) return
        toast.error('Generation failed — is the API running?')
      })
  }, [options, nonce], 250)

  const handleCopy = async () => {
    if (!password) return
    if (await copyToClipboard(password)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } else {
      toast.error('Copy failed')
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
      <header className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight mb-1.5">Generator</h1>
        <p className="text-sm text-ink-muted">
          Generated server-side with a CSPRNG. Updates as you adjust the options.
        </p>
      </header>

      {/* Output */}
      <section className="card p-5 mb-5">
        <div className="flex items-start gap-3">
          <output
            aria-live="polite"
            className="flex-1 font-mono text-base sm:text-lg leading-relaxed break-all min-h-[3.5rem] text-ink"
          >
            {password || <span className="text-ink-faint">…</span>}
          </output>
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => setNonce(n => n + 1)}
              aria-label="Regenerate"
              title="Regenerate"
              className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-muted transition-colors"
            >
              <HiRefresh className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              onClick={handleCopy}
              aria-label={copied ? 'Copied' : 'Copy to clipboard'}
              title="Copy"
              className={`p-2 rounded-lg transition-colors ${
                copied ? 'text-accent' : 'text-ink-muted hover:text-ink hover:bg-surface-muted'
              }`}
            >
              {copied ? <HiCheck className="w-4 h-4" aria-hidden="true" /> : <HiClipboardCopy className="w-4 h-4" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {analysis && (
          <div className="mt-4 pt-4 border-t border-surface-border">
            <StrengthMeter strength={analysis.strength} score={analysis.score} compact />
            <p className="font-mono text-[11px] text-ink-muted mt-2">
              {analysis.entropy} bits · {analysis.length} chars
            </p>
          </div>
        )}
      </section>

      {/* Options */}
      <section className="card p-5 space-y-6">
        <div className="flex p-0.5 bg-surface-inset rounded-lg border border-surface-border w-full sm:w-72">
          {['password', 'passphrase'].map(type => (
            <button
              key={type}
              onClick={() => set('type', type)}
              aria-pressed={options.type === type}
              className={`flex-1 py-1.5 rounded-md text-[13px] font-medium capitalize transition-colors ${
                options.type === type
                  ? 'bg-surface-muted text-ink border border-surface-border'
                  : 'text-ink-muted hover:text-ink-secondary border border-transparent'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {options.type === 'password' ? (
          <>
            <div>
              <div className="flex items-baseline justify-between mb-2.5">
                <label htmlFor="gen-length" className="eyebrow">Length</label>
                <span className="font-mono text-sm text-accent">{options.length}</span>
              </div>
              <input
                id="gen-length"
                type="range"
                min="8"
                max="64"
                value={options.length}
                onChange={e => set('length', parseInt(e.target.value, 10))}
                className="w-full"
              />
              <div className="flex justify-between font-mono text-[10px] text-ink-faint mt-1">
                <span>8</span><span>64</span>
              </div>
            </div>

            <div>
              <p className="eyebrow mb-2.5">Characters</p>
              <div className="flex flex-wrap gap-2">
                {CHARSETS.map(({ key, label }) => {
                  const on = options[key]
                  const locked = on && checkedCount === 1
                  return (
                    <button
                      key={key}
                      onClick={() => !locked && set(key, !on)}
                      aria-pressed={on}
                      disabled={locked}
                      title={locked ? 'At least one character set is required' : undefined}
                      className={`px-3 py-1.5 rounded-lg font-mono text-[13px] border transition-colors ${
                        on
                          ? 'text-ink bg-surface-muted border-surface-edge'
                          : 'text-ink-faint border-surface-border hover:text-ink-muted'
                      } ${locked ? 'cursor-default' : ''}`}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
          </>
        ) : (
          <div>
            <div className="flex items-baseline justify-between mb-2.5">
              <label htmlFor="gen-words" className="eyebrow">Words</label>
              <span className="font-mono text-sm text-accent">{options.wordCount}</span>
            </div>
            <input
              id="gen-words"
              type="range"
              min="3"
              max="8"
              value={options.wordCount}
              onChange={e => set('wordCount', parseInt(e.target.value, 10))}
              className="w-full"
            />
            <div className="flex justify-between font-mono text-[10px] text-ink-faint mt-1">
              <span>3</span><span>8</span>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

export default Generator
